import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { TICKET_NUMBER_PATTERN, type TicketStaffView } from '@traceio-org/platform-contracts';
import type { TicketStaffUpdateInput } from '@traceio-org/platform-client';
import { PageHeading } from '@traceio-org/ui';
import { describeWriteFailure, type WriteFailure } from '../../api';
import { AdminPage } from '../../components/AdminPage';
import { ActionLink, AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../../components/feedback';
import { TextAreaField } from '../../components/fields';
import { DateTimeText } from '../../components/records';
import { WriteDialog, WriteFailureMessage } from '../../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../../context';
import { productName } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { TicketComposer } from './TicketComposer';
import { TicketContextPanel } from './TicketContextPanel';
import { TicketLinkDialog } from './TicketLinkDialog';
import { TicketProperties, type PropertyMenu } from './TicketProperties';
import { TicketThread } from './TicketThread';
import type { QueueNoticeState } from './TicketsPage';
import { CLOSED_STATUSES, URGENCY_LABELS } from './ticketFormat';
import { useTicketKeys } from './useTicketKeys';

const NOTE_MAX = 2000;

/**
 * Who the ticket can be assigned to (GET /v1/admin/ticket-assignees: active staff whose role works tickets), the
 * caller first. Read only by staff who work tickets; while it loads (or if it fails) the caller alone is offered.
 */
function useAssignees(enabled: boolean) {
  const { api } = useAdmin();
  const { person } = useStaff();
  const read = useAdminRead(enabled ? 'ticket-assignees' : null, (signal) => api.listTicketAssignees(signal));
  return useMemo(() => {
    const me = { value: person.userId, label: `${person.displayName} (you)` };
    const others = (read.data?.items ?? [])
      .filter((m) => m.id !== person.userId)
      .map((m) => ({ value: m.id, label: m.displayName ?? 'Staff member' }));
    return [me, ...others];
  }, [person.userId, person.displayName, read.data]);
}

function Meta({ ticket }: { ticket: TicketStaffView }) {
  return (
    <span className="admin-heading-meta admin-ticket-meta">
      <span>
        Filed by {ticket.requester.name ?? 'someone'}
        {ticket.organizationName ? ` (${ticket.organizationName})` : ''}
        {ticket.product ? ` from ${productName(ticket.product)}` : ''},{' '}
        <DateTimeText iso={ticket.createdAt} />
      </span>
      {ticket.urgency ? <span>Customer urgency: {URGENCY_LABELS[ticket.urgency]}</span> : null}
    </span>
  );
}

/**
 * One ticket as Trace works it (/tickets/:number), a page of its own (no drawer): the title and who filed it, the
 * captured context, the thread (replies, Trace notes, events), the composer, and the properties column. Staff who work
 * tickets change status, priority, assignee, labels and links, and hand the ticket back to the customer's help desk;
 * every staff reader replies and writes Trace notes. Keyboard: S status, P priority, A assignee, L labels.
 */
export function TicketPage() {
  const { number = '' } = useParams();
  const navigate = useNavigate();
  const { api } = useAdmin();
  const abilities = useAbilities();
  const valid = TICKET_NUMBER_PATTERN.test(number);
  const key = valid && abilities.readTickets ? `ticket\u0000${number}` : null;
  const read = useAdminRead(key, (signal) => api.getTicket(number, signal));
  const ticket = read.dataKey === key && !read.failure ? read.data : null;
  const failure = !valid ? { kind: 'not-found' as const } : read.failure;
  const labels = useAdminRead(abilities.readTickets ? 'ticket-labels' : null, (signal) =>
    api.listTicketLabels(signal),
  );
  const settings = useAdminRead(abilities.readTickets ? 'ticket-settings' : null, (signal) =>
    api.readTicketSettings(signal),
  );
  const canManage = abilities.manageTickets;
  const assignees = useAssignees(canManage);
  const now = useNow();

  const [openMenu, setOpenMenu] = useState<PropertyMenu | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [writeFailure, setWriteFailure] = useState<WriteFailure | null>(null);
  const [handingBack, setHandingBack] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkKey, setLinkKey] = useState(0);

  // Writes made from the page itself (properties, links): sent at once, then the ticket is read again.
  const write = async (send: () => Promise<unknown>, description: string) => {
    setWriteFailure(null);
    try {
      await send();
    } catch (error) {
      const described = describeWriteFailure(error);
      if (described.kind !== 'saved-unreadable') {
        setWriteFailure(described);
        read.retry();
        return;
      }
    }
    setNotice(description);
    read.retry();
  };
  const change = (patch: TicketStaffUpdateInput, description: string) =>
    void write(() => api.updateTicket(number, patch), description);

  useTicketKeys(
    canManage && ticket
      ? {
          s: () => setOpenMenu('status'),
          p: () => setOpenMenu('priority'),
          a: () => setOpenMenu('assignee'),
          l: () => setOpenMenu('labels'),
        }
      : {},
    // Off while a dialog or a property menu is open: their own keys (letters typed, arrows) are theirs.
    !handingBack && !linking && openMenu === null,
  );

  const closed = ticket?.traceStatus ? CLOSED_STATUSES.includes(ticket.traceStatus) : false;
  const canHandBack = Boolean(canManage && ticket?.escalated && !ticket.handedBack && !closed);
  const notFound = failure?.kind === 'not-found' || failure?.kind === 'invalid-request';
  const title = ticket ? ticket.title : notFound ? 'Ticket not found' : number || 'Ticket';

  return (
    <AdminPage title={ticket ? `${ticket.number} ${ticket.title}` : title}>
      <nav className="admin-breadcrumb" aria-label="Breadcrumb">
        <Link to="/tickets">Tickets</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{number}</span>
      </nav>
      <PageHeading
        eyebrow={ticket ? ticket.number : null}
        title={title}
        description={
          ticket ? (
            <Meta ticket={ticket} />
          ) : notFound ? (
            'Trace has no ticket with this number in its queue. It may have been handed back to the customer, or the link is wrong.'
          ) : null
        }
        actions={
          canHandBack ? (
            <button
              type="button"
              className="admin-action"
              onClick={() => {
                setNote('');
                setNoteError(null);
                setHandingBack(true);
              }}
            >
              Hand back to help desk
            </button>
          ) : null
        }
      />
      <SavedNotice message={notice} />
      {writeFailure ? <WriteFailureMessage failure={writeFailure} /> : null}
      {!abilities.readTickets ? (
        <p className="admin-muted">Your staff role does not include tickets.</p>
      ) : null}
      {failure && notFound ? (
        <div className="admin-problem-actions">
          <ActionLink primary to="/tickets">
            Back to tickets
          </ActionLink>
        </div>
      ) : null}
      {failure && !notFound ? (
        <AdminProblem failure={failure} subject="this ticket" onRetry={read.retry} />
      ) : null}
      {abilities.readTickets && !ticket && !failure ? <LoadingBlock label="Loading the ticket" /> : null}
      {ticket ? (
        <div className="admin-ticket-layout" aria-busy={read.loading || undefined}>
          <div className="admin-ticket-main">
            {ticket.escalationNote ? (
              <section className="admin-ticket-escalation-note" aria-labelledby="ticket-escalation-note">
                <h2 id="ticket-escalation-note" className="admin-ticket-escalation-note__title">
                  Help desk&apos;s note for Trace
                </h2>
                <p className="admin-ticket-escalation-note__body">{ticket.escalationNote}</p>
              </section>
            ) : null}
            {ticket.context ? (
              <TicketContextPanel context={ticket.context} capturedAt={ticket.createdAt} />
            ) : null}
            <h2 className="admin-sr-only">Activity</h2>
            <TicketThread messages={ticket.messages} events={ticket.events} />
            {abilities.replyTickets ? (
              <TicketComposer
                ticket={ticket}
                canSetStatus={canManage}
                onSent={(kind) => {
                  setNotice(kind === 'reply' ? 'Reply sent.' : 'Trace note added.');
                  read.retry();
                }}
              />
            ) : null}
          </div>
          <TicketProperties
            ticket={ticket}
            now={now}
            canManage={canManage}
            labels={labels.data?.items ?? []}
            assignees={assignees}
            settings={settings.data}
            openMenu={openMenu}
            onOpenMenu={setOpenMenu}
            onChange={change}
            onAddLink={() => {
              setLinkKey((k) => k + 1);
              setLinking(true);
            }}
            onRemoveLink={(linkId, ref) =>
              void write(() => api.removeTicketLink(number, linkId), `Link ${ref} removed.`)
            }
          />
        </div>
      ) : null}
      {ticket && canManage ? (
        <TicketLinkDialog
          key={linkKey}
          open={linking}
          onClose={() => setLinking(false)}
          ticketNumber={ticket.number}
          onLink={(request) => api.addTicketLink(number, request)}
          onSaved={(description) => {
            setNotice(description);
            read.retry();
          }}
        />
      ) : null}
      {ticket && canHandBack ? (
        <WriteDialog
          open={handingBack}
          onClose={() => setHandingBack(false)}
          title={`Hand ${ticket.number} back to the help desk?`}
          description="It leaves Trace's queue. The customer's help desk reads your note and takes it from here."
          submitLabel="Hand back"
          onSubmit={async () => {
            const text = note.trim();
            const error = !text
              ? 'Write a note for the help desk.'
              : text.length > NOTE_MAX
                ? `Write at most ${NOTE_MAX} characters.`
                : null;
            setNoteError(error);
            if (error) return 'invalid';
            await api.handBackTicket(ticket.number, text);
            return undefined;
          }}
          onSaved={() =>
            navigate('/tickets', {
              state: {
                notice: `${ticket.number} is back with the customer's help desk.`,
              } satisfies QueueNoticeState,
            })
          }
        >
          <TextAreaField
            label="Note for the help desk"
            value={note}
            onChange={setNote}
            error={noteError}
            maxLength={NOTE_MAX}
            rows={4}
            hint="The customer reads it, for example: this is a setting on your side, here's how to change it."
            required
          />
        </WriteDialog>
      ) : null}
    </AdminPage>
  );
}
